import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { ScheduleOrientationSessionType } from 'src/app/compliance/models/schedule-orientation-session-type';
import { MultiBundleStatus, ScheduleOrientation } from 'src/app/compliance/models/schedule-orientation.model';

import {
    addScheduleOrientationDateForCandidate, addScheduleOrientationSessionDateForCandidateFail, addScheduleOrientationSessionDateForCandidateSuccess, cancelScheduleOrientation, cancelScheduleOrientationFail, cancelScheduleOrientationSuccess, loadActionRequiredOrientationSessionsCount, loadActionRequiredOrientationSessionsCountFail, loadActionRequiredOrientationSessionsCountSuccess, loadDoesAnyOrientationExist, loadDoesAnyOrientationExistFail, loadDoesAnyOrientationExistSuccess, loadFacilityScheduleOrientationPermission, loadFacilityScheduleOrientationPermissionFail, loadFacilityScheduleOrientationPermissionSuccess, loadMultiBundlesStatuses, loadMultiBundlesStatusesFail, loadMultiBundlesStatusesSuccess, loadScheduleOrientation,
    loadScheduleOrientationFail, loadScheduleOrientationSessions, loadScheduleOrientationSessionsFail, loadScheduleOrientationSessionsSuccess, loadScheduleOrientationSessionTypes, loadScheduleOrientationSessionTypesFail, loadScheduleOrientationSessionTypesSuccess, loadScheduleOrientationSuccess, loadSessionsNextDay, loadSessionsNextDayFail, loadSessionsNextDaySuccess, multiDayAskForNewAvailabilityDates, multiDayAskForNewAvailabilityDatesFail, multiDayAskForNewAvailabilityDatesSuccess, removeScheduleOrientation, removeScheduleOrientationFail, removeScheduleOrientationSuccess, rescheduleMultiDayOrientations, rescheduleMultiDayOrientationsFail, rescheduleMultiDayOrientationsSuccess, saveNewOrientation, saveNewOrientationFail, saveNewOrientationSuccess, sendMultiDayOrientationToMyAya, sendMultiDayOrientationToMyAyaFail, sendMultiDayOrientationToMyAyaSuccess, setAskForNewAvailabilityScheduleOrientation,
    setAskForNewAvailabilityScheduleOrientationFail,
    setAskForNewAvailabilityScheduleOrientationSuccess, setNoteScheduleOrientation, setNoteScheduleOrientationFail, setNoteScheduleOrientationSuccess, setRescheduleScheduleOrientation, setRescheduleScheduleOrientationFail, setRescheduleScheduleOrientationSuccess, setScheduleOrientation, setScheduleOrientationDateForCandidate, setScheduleOrientationFail, setScheduleOrientationMultiDayForCandidate,
    setScheduleOrientationMultiDayForCandidateFail,
    setScheduleOrientationMultiDayForCandidateSuccess, setScheduleOrientationSessionDateForCandidateFail, setScheduleOrientationSessionDateForCandidateSuccess, setScheduleOrientationSuccess, setSendToMyAyaScheduleOrientation, setSendToMyAyaScheduleOrientationFail, setSendToMyAyaScheduleOrientationSuccess, updateNoteOfScheduleOrientation, updateNoteOfScheduleOrientationFail, updateNoteOfScheduleOrientationSuccess, updatePresetDatesOfScheduleOrientation, updatePresetDatesOfScheduleOrientationFail, updatePresetDatesOfScheduleOrientationSuccess, updateScheduleOrientationAvailability, updateScheduleOrientationAvailabilityFail, updateScheduleOrientationAvailabilitySuccess
} from 'src/app/compliance/store/actions/schedule-orientation.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { PagingToken } from 'src/app/shared/models/core';
import { ScheduleOrientationNSwagMigrationService } from '../../api/migration/schedule-orientation-nswag-migration.service';


@Injectable()
export class ScheduleOrientationEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _scheduleOrientationService: ScheduleOrientationNSwagMigrationService
    ) {}

    scheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.getScheduleOrientation(action.contractId).pipe(
                    map((res: ScheduleOrientation) => {
                        const selectedDate = res?.scheduleOrientationCandidateDates?.filter(
                            (d) => d.isSelected === true
                        );
                        if (selectedDate?.length > 0) {
                            res.scheduleOrientationCandidateDates = selectedDate;
                        }
                        return loadScheduleOrientationSuccess({ scheduleOrientation: res });
                    }),
                    catchError((result) => of(loadScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    rescheduleScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setRescheduleScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.postRescheduleOrientation(action.scheduleOrientationId).pipe(
                    map((res: ScheduleOrientation) =>
                        setRescheduleScheduleOrientationSuccess({ scheduleOrientation: res })
                    ),
                    catchError((result) => of(setRescheduleScheduleOrientationFail({ error: result })))
                )
            ),
            concatMap((action) => {
                if (action.type === setRescheduleScheduleOrientationSuccess.type) {
                    const contractId = action.scheduleOrientation.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });

    askForNewAvailabilityScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setAskForNewAvailabilityScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService
                    .postAskForNewAvailabilityOrientation(action.scheduleOrientationId)
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            setAskForNewAvailabilityScheduleOrientationSuccess({
                                scheduleOrientation: res,
                                contractId: res.contractId
                            })
                        ),
                        catchError((result) => of(setAskForNewAvailabilityScheduleOrientationFail({ error: result })))
                    )
            )
        );
    });

    scheduleOrientationSessions$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadScheduleOrientationSessions, saveNewOrientationSuccess),
            switchMap((action) =>
                this._scheduleOrientationService.getScheduleOrientationSessions(action.contractId, action.args).pipe(
                    map((res: PagingToken<ScheduleOrientation[]>) => {
                        return loadScheduleOrientationSessionsSuccess({ scheduleOrientationSessions: res });
                    }),
                    catchError((result) => of(loadScheduleOrientationSessionsFail({ error: result })))
                )
            )
        );
    });

    actionRequiredOrientationSessionsCount$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                loadActionRequiredOrientationSessionsCount,
                saveNewOrientationSuccess,
                addScheduleOrientationSessionDateForCandidateSuccess,
                setAskForNewAvailabilityScheduleOrientationSuccess,
                setSendToMyAyaScheduleOrientationSuccess,
                setScheduleOrientationSessionDateForCandidateSuccess
            ),
            switchMap((action) =>
                this._scheduleOrientationService.getActionRequiredOrientationSessionsCount(action.contractId).pipe(
                    map((res: number) => {
                        return loadActionRequiredOrientationSessionsCountSuccess({ count: res });
                    }),
                    catchError((result) => of(loadActionRequiredOrientationSessionsCountFail({ error: result })))
                )
            )
        );
    });

    scheduleOrientationSessionTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadScheduleOrientationSessionTypes),
            switchMap(() =>
                this._scheduleOrientationService.getScheduleOrientationSessionTypes().pipe(
                    map((res: ScheduleOrientationSessionType[]) => {
                        return loadScheduleOrientationSessionTypesSuccess({ scheduleOrientationSessionTypes: res });
                    }),
                    catchError((result) => of(loadScheduleOrientationSessionTypesFail({ error: result })))
                )
            )
        );
    });

    setScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.postScheduleOrientation(action.contractId, action.note).pipe(
                    map((res: ScheduleOrientation) => setScheduleOrientationSuccess({ scheduleOrientation: res })),
                    catchError((result) => of(setScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    putScheduleOrientationDateForCandidate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setScheduleOrientationDateForCandidate),
            switchMap((action) =>
                this._scheduleOrientationService.putScheduleOrientationDateForCandidate(action.dateId).pipe(
                    map((res: ScheduleOrientation) =>
                        setScheduleOrientationSessionDateForCandidateSuccess({
                            scheduleOrientation: res,
                            contractId: res.contractId
                        })
                    ),
                    catchError((result) => of(setScheduleOrientationSessionDateForCandidateFail({ error: result })))
                )
            )
        );
    });

    addScheduleOrientationDateForCandidate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(addScheduleOrientationDateForCandidate),
            switchMap((action) =>
                this._scheduleOrientationService
                    .postScheduleOrientationDateForCandidate(action.scheduleOrientationId, action.date)
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            addScheduleOrientationSessionDateForCandidateSuccess({
                                scheduleOrientation: res,
                                contractId: res.contractId
                            })
                        ),
                        catchError((result) => of(addScheduleOrientationSessionDateForCandidateFail({ error: result })))
                    )
            )
        );
    });

    removeScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(removeScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.removeScheduleOrientation(action.contractId).pipe(
                    map(() => removeScheduleOrientationSuccess()),
                    catchError((result) => of(removeScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    loadFacilityScheduleOrientationPermission$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadFacilityScheduleOrientationPermission),
            switchMap((action) =>
                this._scheduleOrientationService.getFacilityScheduleOrientationPermission(action.facilityId).pipe(
                    map((res: number) =>
                        loadFacilityScheduleOrientationPermissionSuccess({ orientationPermissionId: res })
                    ),
                    catchError((result) => of(loadFacilityScheduleOrientationPermissionFail({ error: result })))
                )
            )
        );
    });

    saveNewOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(saveNewOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.postAddNewOrientation(action.contractId, action.newOrientation).pipe(
                    map(() =>
                        saveNewOrientationSuccess({
                            contractId: action.contractId,
                            args: action.args,
                            showInMayAya: action.showInMayAya
                        })
                    ),
                    catchError((result) => of(saveNewOrientationFail({ error: result })))
                )
            )
        );
    });

    cancelScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(cancelScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.cancelScheduleOrientation(action.scheduleOrientationId).pipe(
                    map((result: ScheduleOrientation) =>
                        cancelScheduleOrientationSuccess({ scheduleOrientation: result })
                    ),
                    catchError((result) => of(cancelScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    updateNote$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setNoteScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.patchUpdateNote(action.scheduleOrientationId, action.note).pipe(
                    map(() =>
                        setNoteScheduleOrientationSuccess({
                            scheduleOrientationId: action.scheduleOrientationId,
                            note: action.note
                        })
                    ),
                    catchError((result) => of(setNoteScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    sendToMyAyaScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setSendToMyAyaScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.patchSendOrientationToMyAya(action.scheduleOrientationId).pipe(
                    map((scheduleOrientation: ScheduleOrientation) =>
                        setSendToMyAyaScheduleOrientationSuccess({
                            scheduleOrientation: scheduleOrientation,
                            contractId: scheduleOrientation.contractId
                        })
                    ),
                    catchError((result) => of(setSendToMyAyaScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    sessionsNextDay$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadSessionsNextDay),
            switchMap((action) =>
                this._scheduleOrientationService.getNextDayForSessionType(action.contractId, action.sessionTypeId).pipe(
                    map((res: number) => {
                        return loadSessionsNextDaySuccess({ day: res });
                    }),
                    catchError((result) => of(loadSessionsNextDayFail({ error: result })))
                )
            )
        );
    });

    doesAnyOrientationExist$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadDoesAnyOrientationExist),
            switchMap((action) =>
                this._scheduleOrientationService.doesAnyOrientationExist(action.contractId).pipe(
                    map((res: boolean) => {
                        return loadDoesAnyOrientationExistSuccess({ result: res });
                    }),
                    catchError((result) => of(loadDoesAnyOrientationExistFail({ error: result })))
                )
            )
        );
    });

    multiBundlesStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadMultiBundlesStatuses),
            switchMap((action) =>
                this._scheduleOrientationService.GetMultiBundlesStatuses(action.contractId).pipe(
                    map((res: MultiBundleStatus[]) => {
                        return loadMultiBundlesStatusesSuccess({ multiBundleStatuses: res });
                    }),
                    catchError((result) => of(loadMultiBundlesStatusesFail({ error: result })))
                )
            )
        );
    });

    updateScheduleOrientationAvailability$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateScheduleOrientationAvailability),
            switchMap((action) =>
                this._scheduleOrientationService.putScheduleOrientationAvailability(action.updateOrientationDay).pipe(
                    map((res: ScheduleOrientation) =>
                        updateScheduleOrientationAvailabilitySuccess({ scheduleOrientation: res })
                    ),
                    catchError((result) => of(updateScheduleOrientationAvailabilityFail({ error: result })))
                )
            ),
            concatMap((action) => {
                if (action.type === updateScheduleOrientationAvailabilitySuccess.type) {
                    const contractId = action.scheduleOrientation.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });

    putScheduleOrientationMultiDayForCandidate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(setScheduleOrientationMultiDayForCandidate),
            switchMap((action) =>
                this._scheduleOrientationService
                    .putScheduleOrientationMultiDayForCandidate(
                        action.firstDayOrientationId,
                        action.candidateAvailabilityDateArray
                    )
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            setScheduleOrientationMultiDayForCandidateSuccess({ scheduleOrientations: res })
                        ),
                        catchError((result) => of(setScheduleOrientationMultiDayForCandidateFail({ error: result })))
                    )
            ),
            concatMap((action) => {
                if (action.type === setScheduleOrientationMultiDayForCandidateSuccess.type) {
                    const contractId = action.scheduleOrientations.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });

    multiDayAskForNewAvailabilityDates$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(multiDayAskForNewAvailabilityDates),
            switchMap((action) =>
                this._scheduleOrientationService
                    .putMultiDayAskForNewAvailabilityDates(action.firstDayOrientationId, action.updateOrientationDays)
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            multiDayAskForNewAvailabilityDatesSuccess({ scheduleOrientation: res })
                        ),
                        catchError((result) => of(multiDayAskForNewAvailabilityDatesFail({ error: result })))
                    )
            ),
            concatMap((action) => {
                if (action.type === multiDayAskForNewAvailabilityDatesSuccess.type) {
                    const contractId = action.scheduleOrientation.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });

    updatePresetDatesOfScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updatePresetDatesOfScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService.patchPresetDatesOfScheduleOrientation(action.presetDateDto).pipe(
                    map((res: ScheduleOrientation) =>
                        updatePresetDatesOfScheduleOrientationSuccess({ scheduleOrientation: res })
                    ),
                    catchError((result) => of(updatePresetDatesOfScheduleOrientationFail({ error: result })))
                )
            )
        );
    });

    updateNoteOfScheduleOrientation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateNoteOfScheduleOrientation),
            switchMap((action) =>
                this._scheduleOrientationService
                    .patchNoteOfScheduleOrientation(action.scheduleOrientationId, action.note)
                    .pipe(
                        map((result: ScheduleOrientation) =>
                            updateNoteOfScheduleOrientationSuccess({ scheduleOrientation: result })
                        ),
                        catchError((result) => of(updateNoteOfScheduleOrientationFail({ error: result })))
                    )
            )
        );
    });

    confirmationRequestHasBeenSentToMyAya$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                setScheduleOrientationSessionDateForCandidateSuccess,
                addScheduleOrientationSessionDateForCandidateSuccess,
                setScheduleOrientationMultiDayForCandidateSuccess
            ),
            map(() =>
                showToasterMessage({ message: 'Confirmation request has been sent to MyAya', duration: 5000 })
            )
        );
    });

    setSendToMyAyaScheduleOrientationSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                setSendToMyAyaScheduleOrientationSuccess,
                sendMultiDayOrientationToMyAyaSuccess,
                saveNewOrientationSuccess
            ),
            map((action) => {
                if (action.type !== saveNewOrientationSuccess.type) {
                    return showToasterMessage({
                        message: 'Availability request has been sent to MyAya.',
                        duration: 5000
                    });
                } else if (action.type === saveNewOrientationSuccess.type && action.showInMayAya) {
                    return showToasterMessage({
                        message: 'Availability request has been sent to MyAya.',
                        duration: 5000
                    });
                } else {
                    return { type: '[No Operation]' }; // Optional no-op action
                }
            })
        );
    });
    
    rescheduleMultiDayOrientations$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(rescheduleMultiDayOrientations),
            switchMap((action) =>
                this._scheduleOrientationService
                    .postRescheduleMultiDayOrientations(action.firstDayOrientationId, action.orientationIds)
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            rescheduleMultiDayOrientationsSuccess({ scheduleOrientation: res })
                        ),
                        catchError((result) => of(rescheduleMultiDayOrientationsFail({ error: result })))
                    )
            ),
            concatMap((action) => {
                if (action.type === rescheduleMultiDayOrientationsSuccess.type) {
                    const contractId = action.scheduleOrientation.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });

    sendMultiDayOrientationToMyAya$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(sendMultiDayOrientationToMyAya),
            switchMap((action) =>
                this._scheduleOrientationService
                    .patchSendMultiDayOrientationToMyAya(action.firstDayOrientationId, action.orientationIds)
                    .pipe(
                        map((res: ScheduleOrientation) =>
                            sendMultiDayOrientationToMyAyaSuccess({ scheduleOrientation: res })
                        ),
                        catchError((result) => of(sendMultiDayOrientationToMyAyaFail({ error: result })))
                    )
            ),
            concatMap((action) => {
                if (action.type === sendMultiDayOrientationToMyAyaSuccess.type) {
                    const contractId = action.scheduleOrientation.contractId;
                    return [action, loadActionRequiredOrientationSessionsCount({ contractId })];
                }
                return [action];
            })
        );
    });
}
